import { defineStore } from 'pinia'
import { decodePermissions, encodePermissions } from '@erc725/erc725.js'
import { ALL_PERMISSIONS, CALLTYPE, PERMISSIONS } from '@lukso/lsp6-contracts'

import { Restriction } from '@/shared/models/Restriction'
import { LastPopupState } from '@/shared/models/Profile'
import { logError } from '@/shared/logger'
import { PermissionName } from '@/business-logic/profile'

type Permissions = { [Key in PermissionName]: boolean }

export type AllowedCallState = {
  restrictions: Restriction[]
  permissions: Permissions
  originalPermissions: Permissions
  assignPermissions: Permissions
  recurse: number
}

const superNameToNameMap: {
  superName: PermissionName
  name: PermissionName
}[] = [
  { superName: 'SUPER_CALL', name: 'CALL' },
  { superName: 'SUPER_DELEGATECALL', name: 'DELEGATECALL' },
  { superName: 'SUPER_SETDATA', name: 'SETDATA' },
  { superName: 'SUPER_TRANSFERVALUE', name: 'TRANSFERVALUE' },
  { superName: 'SUPER_STATICCALL', name: 'STATICCALL' },
]

// eslint-disable-next-line sonarjs/cognitive-complexity
function doPermissionsChange() {
  const appPermissionStore = useAppPermissionStore()

  const enables: PermissionName[] = []
  // Default X permission if SUPER_X is on.
  for (const { superName, name } of superNameToNameMap) {
    if (
      appPermissionStore.permissions[superName] &&
      !appPermissionStore.permissions[name]
    ) {
      enables.push(name)
    }
  }
  if (enables.length > 0) {
    appPermissionStore.enable(enables)
  }

  // Enable all SUPER_ permissions to start.
  appPermissionStore.enable([
    'SUPER_SETDATA',
    'SUPER_CALL',
    'SUPER_STATICCALL',
    'SUPER_DELEGATECALL',
    'SUPER_SETDATA',
    'SUPER_TRANSFERVALUE',
  ])

  // Collect all NON-SUPER permissions that have restrictions here.
  const hasRestrictions: PermissionName[] = []

  // Disable any SUPER_ permissions for each type of allowed address (if they exist)
  for (const restriction of appPermissionStore.restrictions) {
    if (restriction.status === 'DELETED') {
      continue
    }
    switch (restriction.type) {
      case 'SETDATA': {
        appPermissionStore.disable(['SUPER_SETDATA'])
        hasRestrictions.push('SETDATA')
        break
      }
      case 'CALL': {
        if (Number(restriction.calls) & Number(CALLTYPE.CALL)) {
          appPermissionStore.disable(['SUPER_CALL'])
          hasRestrictions.push('CALL')
        }
        if (Number(restriction.calls) & Number(CALLTYPE.STATICCALL)) {
          appPermissionStore.disable(['SUPER_STATICCALL'])
          hasRestrictions.push('STATICCALL')
        }
        if (Number(restriction.calls) & Number(CALLTYPE.DELEGATECALL)) {
          appPermissionStore.disable(['SUPER_DELEGATECALL'])
          hasRestrictions.push('DELEGATECALL')
        }
        if (Number(restriction.calls) & Number(CALLTYPE.VALUE)) {
          appPermissionStore.disable(['SUPER_TRANSFERVALUE'])
          hasRestrictions.push('TRANSFERVALUE')
        }
        break
      }
      default: {
        logError('unknown address type', restriction)
        break
      }
    }
  }

  // Disably any SUPER_ permissions if the corresponding permission is off
  if (!appPermissionStore.permissions.CALL) {
    appPermissionStore.disable(['SUPER_CALL'])
  }
  if (!appPermissionStore.permissions.STATICCALL) {
    appPermissionStore.disable(['SUPER_STATICCALL'])
  }
  if (!appPermissionStore.permissions.DELEGATECALL) {
    appPermissionStore.disable(['SUPER_DELEGATECALL'])
  }
  if (!appPermissionStore.permissions.TRANSFERVALUE) {
    appPermissionStore.disable(['SUPER_TRANSFERVALUE'])
  }
  if (!appPermissionStore.permissions.SETDATA) {
    appPermissionStore.disable(['SUPER_SETDATA'])
  }

  appPermissionStore.enableRaw(hasRestrictions)
}

export function calcDisplayPermissions(permissions: Permissions) {
  const displayPermissions = Object.assign({}, permissions)
  for (const { superName, name } of superNameToNameMap) {
    if (displayPermissions[superName] && !displayPermissions[name]) {
      displayPermissions[name] = true
    }
  }
  return displayPermissions
}

// Add special assignPermissions which does the reverse calculation of calcDisplayPermissions
// So during init we set NON-SUPER-PERMISSION for each SUPER-PERMISSION that's true.
// During edit we set SUPER-PERMISSION only for those perms that don't have RESTRICTIONS, but keep NON-SUPER-PERMISSIONS matching the UI.
// In addition we keep track of an "assignPermissions" object which will keep NON-SUPER-PERMISSIONS off if there are no restrictions for it.
// Although those enabled NON-SUPER-PERMISSIONS don't cause any trouble they "look wrong".

export const useAppPermissionStore = defineStore('appPermissions', {
  state: () => {
    const permissions = decodePermissions(ALL_PERMISSIONS)
    return {
      restrictions: [],
      permissions,
      assignPermissions: Object.assign({}, permissions),
      originalPermissions: Object.assign({}, permissions),
      recurse: 0,
    } as AllowedCallState
  },
  getters: {
    hasChanges: (state: AllowedCallState) => {
      return (
        state.restrictions.some(
          restrictions =>
            restrictions.status === 'DELETED' || restrictions.status === 'NEW'
        ) ||
        encodePermissions(calcDisplayPermissions(state.permissions)) !==
          encodePermissions(calcDisplayPermissions(state.originalPermissions))
      )
    },
    getAddresses: (state: AllowedCallState) => {
      return () =>
        state.restrictions
          .filter(allowedCall => allowedCall.status !== 'DELETED')
          .filter(({ type }) => type === 'CALL')
          .map(restriction =>
            restriction.type === 'CALL' ? restriction.address : undefined
          )
          .filter(Boolean)
    },
    changedTypes: (state: AllowedCallState) => {
      const changedTypes = state.restrictions
        .filter(
          allowedCall =>
            allowedCall.status === 'NEW' || allowedCall.status === 'DELETED'
        )
        .map(allowedCall => allowedCall.type)
      return [...new Set(changedTypes)]
    },
    encodedPermissions: (state: AllowedCallState) => {
      return encodePermissions(state.assignPermissions)
    },
  },
  actions: {
    setPermissions(activePermissions: PermissionName[]) {
      const permissions: Partial<Permissions> = {}
      for (const key in PERMISSIONS) {
        permissions[key as PermissionName] = activePermissions.includes(
          key as PermissionName
        )
      }
      this.permissions = Object.assign({}, permissions) as Permissions
      this.originalPermissions = permissions as Permissions
    },
    init(restrictions?: Restriction[], lastPopupState?: LastPopupState) {
      const permissions = decodePermissions(ALL_PERMISSIONS)
      this.permissions = Object.assign({}, permissions)
      this.originalPermissions = permissions
      if (!this.restrictions || restrictions != undefined) {
        this.restrictions =
          lastPopupState?.restrictions && lastPopupState.restrictions.length > 0
            ? lastPopupState.restrictions
            : restrictions || []
      }
      if (process.env.NODE_ENV !== 'production') {
        window.store = {
          appPermissions: this,
        }
      }
    },
    rollbackChanges() {
      this.restrictions = this.restrictions.flatMap(allowedCall =>
        allowedCall.status === 'NEW'
          ? []
          : allowedCall.status === 'DELETED'
          ? { ...allowedCall, status: 'OLD' }
          : allowedCall
      )
      this.permissions = Object.assign({}, this.originalPermissions)
      this.onPermissionsChange()
    },
    getAll(): Permissions {
      return calcDisplayPermissions(this.permissions)
    },
    disable(permissions: PermissionName[]) {
      for (const permission of permissions) {
        this.permissions[permission] = false
        const superPerm: PermissionName =
          `SUPER_${permission}` as PermissionName
        if (this.permissions[superPerm]) {
          this.permissions[superPerm] = false
        }
      }
      this.onPermissionsChange()
    },
    enable(permissions: PermissionName[]) {
      for (const permission of permissions) {
        this.permissions[permission] = true
      }
      this.onPermissionsChange()
    },
    enableRaw(permissions: PermissionName[]) {
      // call special enable function which disabled NON-SUPER permissions for any which are no mentioned in the array.

      this.assignPermissions = Object.assign({}, this.permissions)
      for (const { superName, name } of superNameToNameMap) {
        if (this.assignPermissions[superName] && !permissions.includes(name)) {
          console.log(
            'disabling',
            name,
            'because',
            superName,
            'is enabled and there are no restrictions'
          )
          this.assignPermissions[name] = false
        }
      }
    },
    onPermissionsChange() {
      this.recurse++
      try {
        if (this.recurse === 1) {
          doPermissionsChange()
        }
      } finally {
        this.recurse--
      }
    },
  },
})
